import { useQuery } from "@tanstack/react-query";
import axios from "const/axios";
import { API_URL } from "const/constants";
import { groupCreditAdjustmentPairs } from "../../Admissions/AddAdmissions/applicationFlow/creditAdjustmentUtils";

/** Pending per-payment verifications for a client (task-free). */
export const usePendingPaymentVerifications = (clientId, enabled) =>
  useQuery({
    queryKey: ["pending-payment-verifications", clientId],
    queryFn: () =>
      axios
        .get(`${API_URL}/admission-terms/pending-verifications`, { params: { clientId } })
        .then((r) => r.data?.data ?? []),
    // A credit move is two rows but one decision, so every consumer (lists and counts) sees one item.
    select: groupCreditAdjustmentPairs,
    enabled: Boolean(enabled) && !!clientId,
    refetchOnWindowFocus: false,
  });

/** Pending whole-term verifications for a client (AccountVerification, status PENDING). */
export const usePendingTermVerifications = (clientId, enabled) =>
  useQuery({
    queryKey: ["pending-term-verifications", clientId],
    queryFn: () =>
      axios
        .get(`${API_URL}/account-verification/client/${clientId}`)
        .then((r) => (r.data?.data ?? []).filter((v) => v.status === "PENDING")),
    enabled: Boolean(enabled) && !!clientId,
    refetchOnWindowFocus: false,
  });

/** Whole-term verification detail by (admissionId, termIndex). */
export const useTermVerificationDetail = (admissionId, termIndex, enabled) =>
  useQuery({
    queryKey: ["account-verification-by-term", admissionId, termIndex],
    queryFn: () =>
      axios
        .get(`${API_URL}/account-verification/by-term`, { params: { admissionId, termIndex } })
        .then((r) => r.data?.data ?? null),
    enabled: Boolean(enabled) && admissionId != null && termIndex != null,
    refetchOnWindowFocus: false,
  });

/** Term discussion notes for a given admission term. */
export const useTermNotes = (clientId, admissionId, termIndex, enabled) =>
  useQuery({
    queryKey: ["verification-term-notes", clientId, admissionId, termIndex],
    queryFn: () =>
      axios
        .get(`${API_URL}/discussion/${clientId}?type=admission_term_${admissionId}_${termIndex}`)
        .then((r) => r.data?.comments ?? r.data?.data ?? []),
    enabled: Boolean(enabled) && !!clientId && admissionId != null && termIndex != null,
    refetchOnWindowFocus: false,
  });

export const verifyTermVerification = (verificationId) =>
  axios.post(`${API_URL}/account-verification/${verificationId}/verify-and-import`);

export const rejectTermVerification = (verificationId, feedback) =>
  axios.post(`${API_URL}/account-verification/${verificationId}/reject`, { feedback });

export const resubmitTermVerification = (verificationId) =>
  axios.post(`${API_URL}/account-verification/${verificationId}/resubmit`);

/**
 * expectedRevision is the revision the verifier was shown. When the sender has edited the payment
 * since, the server answers 409 with the current version instead of deciding on stale figures.
 */
export const verifyPayment = (paymentId, expectedRevision) =>
  axios.post(`${API_URL}/admission-terms/payments/${paymentId}/verify`, null, {
    params: expectedRevision != null ? { expectedRevision } : undefined,
  });

export const rejectPayment = (paymentId, feedback, expectedRevision) =>
  axios.post(
    `${API_URL}/admission-terms/payments/${paymentId}/reject`,
    { feedback },
    { params: expectedRevision != null ? { expectedRevision } : undefined }
  );

/** The sender corrects a term payment still waiting for verification. payload carries expectedRevision. */
export const editPendingTermPayment = (paymentId, payload) =>
  axios.put(`${API_URL}/admission-terms/payments/${paymentId}/pending`, payload);

/** The sender takes back a term payment (or credit move) still waiting for verification. */
export const withdrawTermPayment = (paymentId, { expectedRevision, reason }) =>
  axios.post(`${API_URL}/admission-terms/payments/${paymentId}/withdraw`, {
    expectedRevision,
    reason,
  });
